import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileSpreadsheet, FileText, Calendar, ArrowLeft, Download,
  TrendingDown, DollarSign, CreditCard, Clock, CheckCircle
} from 'lucide-react';
import { reportsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const PurchaseRegisterPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [registerData, setRegisterData] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    invoice_no: '',
    supplier_name: '',
    gstin: '',
    total: { min: '', max: '' },
    itc_status: []
  });

  useEffect(() => {
    if (businessId) {
      fetchPurchaseRegister();
    }
  }, [fromDate, toDate, businessId]);

  const fetchPurchaseRegister = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.purchaseRegister({ from_date: fromDate, to_date: toDate });
      setRegisterData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const response = await reportsAPI.exportReport({ report_type: 'purchase-register', format, from_date: fromDate, to_date: toDate });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Purchase_Register_${fromDate}_${toDate}.${format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const summary = registerData?.summary || {
    total_purchase_value: 0,
    itc_available: 0,
    itc_claimed: 0,
    itc_pending: 0
  };

  const purchases = registerData?.rows || [];

  // Apply filters
  const filteredPurchases = purchases.filter(p => {
    const matchesInvoice = !columnFilters.invoice_no || 
      p.invoice_no?.toLowerCase().includes(columnFilters.invoice_no.toLowerCase());
    const matchesSupplier = !columnFilters.supplier_name || 
      p.supplier_name?.toLowerCase().includes(columnFilters.supplier_name.toLowerCase());
    const matchesGstin = !columnFilters.gstin || 
      p.gstin?.toLowerCase().includes(columnFilters.gstin.toLowerCase());
    const total = p.total || 0;
    const matchesTotal = (!columnFilters.total?.min || total >= parseFloat(columnFilters.total.min)) &&
      (!columnFilters.total?.max || total <= parseFloat(columnFilters.total.max));
    const matchesStatus = columnFilters.itc_status.length === 0 || 
      columnFilters.itc_status.includes(p.itc_status);
    return matchesInvoice && matchesSupplier && matchesGstin && matchesTotal && matchesStatus;
  });

  const dateRangeLabel = `${new Date(fromDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${new Date(toDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;

  return (
    <div className="h-full flex flex-col" data-testid="purchase-register-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Purchase Register (GST)"
        stats={[
          { icon: DollarSign, label: 'Total Purchases', value: formatCompactCurrency(summary.total_purchase_value), color: 'slate' },
          { icon: CreditCard, label: 'ITC Available', value: formatCompactCurrency(summary.itc_available), color: 'emerald' },
          { icon: CheckCircle, label: 'ITC Claimed', value: formatCompactCurrency(summary.itc_claimed), color: 'blue' },
          { icon: Clock, label: 'ITC Pending', value: formatCompactCurrency(summary.itc_pending), color: 'amber' },
        ]}
        secondaryAction={{
          label: dateRangeLabel,
          icon: Calendar,
          onClick: () => {}
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${businessId}/reports`)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors text-[12px] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
                />
                <span className="text-slate-300">→</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="text-[12px] text-slate-600 bg-transparent border-none focus:outline-none w-[100px]"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleExport('excel')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Excel
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" />
                PDF
              </button>
              <button onClick={() => handleExport('json')} disabled={exporting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                <Download className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading purchase register...</p>
              </div>
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <TrendingDown className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No purchase records found</p>
              <p className="text-[13px] text-slate-400 mt-1">Purchase invoices for selected period will appear here</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Table Header */}
              <div className="flex-shrink-0">
                <table className="tl-table">
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>Date</th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          Invoice No
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.invoice_no}
                            onChange={(val) => setColumnFilters({...columnFilters, invoice_no: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '18%' }}>
                        <div className="flex items-center gap-1">
                          Supplier Name
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.supplier_name}
                            onChange={(val) => setColumnFilters({...columnFilters, supplier_name: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '14%' }}>
                        <div className="flex items-center gap-1">
                          GSTIN
                          <ColumnFilter 
                            type="text" 
                            value={columnFilters.gstin}
                            onChange={(val) => setColumnFilters({...columnFilters, gstin: val})}
                            placeholder="Filter..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>Taxable (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '10%' }}>ITC (₹)</th>
                      <th className="tl-th tl-th-teal text-right" style={{ width: '12%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Total (₹)
                          <ColumnFilter 
                            type="number" 
                            value={columnFilters.total}
                            onChange={(val) => setColumnFilters({...columnFilters, total: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '12%' }}>
                        <div className="flex items-center gap-1">
                          ITC Status
                          <ColumnFilter 
                            type="status" 
                            value={columnFilters.itc_status}
                            onChange={(val) => setColumnFilters({...columnFilters, itc_status: val})}
                            options={[
                              { value: 'Claimed', label: 'Claimed' },
                              { value: 'Pending', label: 'Pending' },
                              { value: 'Rejected', label: 'Rejected' }
                            ]}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Table Body */}
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <tbody className="animate-stagger">
                    {filteredPurchases.map((purchase, idx) => (
                      <tr 
                        key={idx} 
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      >
                        <td className="tl-td tl-cell-regular" style={{ width: '10%' }}>
                          {new Date(purchase.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                        </td>
                        <td className="tl-td tl-cell-primary" style={{ width: '12%' }}>{purchase.invoice_no}</td>
                        <td className="tl-td tl-cell-regular" style={{ width: '18%' }}>{purchase.supplier_name}</td>
                        <td className="tl-td" style={{ width: '14%' }}>
                          {purchase.gstin ? (
                            <span className="text-[11px] font-mono text-slate-600">{purchase.gstin}</span>
                          ) : (
                            <span className="text-[11px] text-slate-400">—</span>
                          )}
                        </td>
                        <td className="tl-td text-right tl-cell-regular" style={{ width: '12%' }}>₹{(purchase.taxable || 0).toLocaleString()}</td>
                        <td className="tl-td text-right text-[13px] font-medium text-emerald-600" style={{ width: '10%' }}>₹{(purchase.itc || 0).toLocaleString()}</td>
                        <td className="tl-td text-right text-[13px] font-semibold text-slate-800" style={{ width: '12%' }}>₹{(purchase.total || 0).toLocaleString()}</td>
                        <td className="tl-td" style={{ width: '12%' }}>
                          {purchase.itc_status === 'Claimed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Claimed
                            </span>
                          ) : purchase.itc_status === 'Pending' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-medium">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-medium">
                              {purchase.itc_status || '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredPurchases.length}</span> of <span className="font-medium text-slate-700">{purchases.length}</span> purchases
                </p>
                <p>
                  Total ITC: <span className="font-medium text-slate-700">₹{summary.itc_available.toLocaleString()}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseRegisterPage;
